import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  checkEmptyInputValue,
  getFormattedFloatNumber,
} from '@pages/AdditionalServices/tabs/DeliveryForm/helpers';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { CustomCargoCalcType, SelectionCustomCargoBaseParameters } from '../types';
import { CargoSelectionItemProps } from './types';

const CargoSelectionBase: React.FC<CargoSelectionItemProps> = ({
  isActionsDisabled,
  isDeletable,
  position,
  selectionCargoParamaters,
  onDeleteSelectionCargoParameters,
  onChangeSelectionCargoParameters,
}) => {
  const { t } = useTranslation('Common');

  const isHeightInputInvalid = checkEmptyInputValue(selectionCargoParamaters.baseParameters.height);
  const isLengthInputInvalid = checkEmptyInputValue(selectionCargoParamaters.baseParameters.length);
  const isQuantityInputInvalid = checkEmptyInputValue(
    selectionCargoParamaters.baseParameters.quantity
  );
  const isWeightInputInvalid = checkEmptyInputValue(selectionCargoParamaters.baseParameters.weight);
  const isWidthInputInvalid = checkEmptyInputValue(selectionCargoParamaters.baseParameters.width);

  const handleChangeCargoSelectionBaseParameters = <
    T extends keyof SelectionCustomCargoBaseParameters
  >(
    key: T,
    value: SelectionCustomCargoBaseParameters[T]
  ) => {
    onChangeSelectionCargoParameters({
      ...selectionCargoParamaters,
      baseParameters: {
        ...selectionCargoParamaters.baseParameters,
        [key]: value,
      },
    });
  };

  const handleChangeCustomCargoCalcType = (type: CustomCargoCalcType) => {
    onChangeSelectionCargoParameters({
      ...selectionCargoParamaters,
      parametersCargoCalcType: type,
    });
  };

  const handleChangeCustomCargoType = (type: 'base' | 'pallet') => {
    onChangeSelectionCargoParameters({
      ...selectionCargoParamaters,
      customCargoType: type,
    });
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <Typography variant={'subtitle2'} color={'#707070'}>
        {t('Common:CargoPlace')} #{position}
      </Typography>
      <Box className={'cargo-types'}>
        <Typography component={'span'} variant={'h5'} className={'cargo-types__title'}>
          {t('Common:CalculationType')}
        </Typography>
        <Button
          size={'small'}
          variant={'outlined'}
          className={'not-selected'}
          color={'inherit'}
          disabled={isActionsDisabled}
          onClick={() => handleChangeCustomCargoCalcType('volume')}
        >
          {t('Common:Consignment')}
        </Button>
        <Button disabled={isActionsDisabled} size={'small'} variant={'outlined'}>
          {t('Common:CargoUnit')}
        </Button>
        <Typography component={'span'} variant={'h5'} className={'cargo-types__title'}>
          {t('Common:InputType')}
        </Typography>
        <Button
          size={'small'}
          className={'not-selected'}
          variant={'outlined'}
          color={'inherit'}
          disabled={isActionsDisabled}
          onClick={() => handleChangeCustomCargoType('pallet')}
        >
          {t('Common:Pallet')}
        </Button>
        <Button disabled={isActionsDisabled} size={'small'} variant={'outlined'}>
          {t('Common:Dimensions')}
        </Button>
      </Box>
      <Box className={'cargo-inputs-block-grid'}>
        <TextField
          error={isWidthInputInvalid}
          focused={isWidthInputInvalid}
          className={'cargo-inputs-block__half-mobile width'}
          variant={'standard'}
          value={selectionCargoParamaters.baseParameters.width}
          disabled={isActionsDisabled}
          onFocus={() =>
            handleChangeCargoSelectionBaseParameters('width', getFormattedFloatNumber(''))
          }
          onChange={(event) =>
            handleChangeCargoSelectionBaseParameters(
              'width',
              getFormattedFloatNumber(event.target.value)
            )
          }
          type={'text'}
          InputProps={{
            inputMode: 'numeric',
            startAdornment: (
              <InputAdornment
                sx={{ color: '#707070', position: 'absolute', pointerEvents: 'none' }}
                position="start"
              >
                <Typography component={'span'} variant={'h5'}>
                  {t('Common:WidthM')}
                </Typography>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          error={isLengthInputInvalid}
          focused={isLengthInputInvalid}
          className={'cargo-inputs-block__half-mobile length'}
          variant={'standard'}
          value={selectionCargoParamaters.baseParameters.length}
          disabled={isActionsDisabled}
          onFocus={() =>
            handleChangeCargoSelectionBaseParameters('length', getFormattedFloatNumber(''))
          }
          onChange={(event) =>
            handleChangeCargoSelectionBaseParameters(
              'length',
              getFormattedFloatNumber(event.target.value)
            )
          }
          type={'text'}
          InputProps={{
            inputMode: 'numeric',
            startAdornment: (
              <InputAdornment
                sx={{ color: '#707070', position: 'absolute', pointerEvents: 'none' }}
                position="start"
              >
                <Typography component={'span'} variant={'h5'}>
                  {t('Common:LengthM')}
                </Typography>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          error={isHeightInputInvalid}
          focused={isHeightInputInvalid}
          fullWidth
          variant={'standard'}
          className={'height'}
          value={selectionCargoParamaters.baseParameters.height}
          disabled={isActionsDisabled}
          onFocus={() =>
            handleChangeCargoSelectionBaseParameters('height', getFormattedFloatNumber(''))
          }
          onChange={(event) =>
            handleChangeCargoSelectionBaseParameters(
              'height',
              getFormattedFloatNumber(event.target.value)
            )
          }
          type={'text'}
          InputProps={{
            inputMode: 'numeric',
            startAdornment: (
              <InputAdornment
                sx={{ color: '#707070', position: 'absolute', pointerEvents: 'none' }}
                position="start"
              >
                <Typography component={'span'} variant={'h5'}>
                  {t('Common:HeightM')}
                </Typography>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          error={isWeightInputInvalid}
          focused={isWeightInputInvalid}
          fullWidth
          variant={'standard'}
          className={'weight'}
          value={selectionCargoParamaters.baseParameters.weight}
          disabled={isActionsDisabled}
          onFocus={() =>
            handleChangeCargoSelectionBaseParameters('weight', getFormattedFloatNumber(''))
          }
          onChange={(event) => {
            handleChangeCargoSelectionBaseParameters(
              'weight',
              getFormattedFloatNumber(event.target.value)
            );
          }}
          type={'text'}
          InputProps={{
            inputMode: 'numeric',
            startAdornment: (
              <InputAdornment
                sx={{ color: '#707070', position: 'absolute', pointerEvents: 'none' }}
                position="start"
              >
                <Typography component={'span'} variant={'h5'}>
                  {t('Common:WeightKg')}
                </Typography>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          error={isQuantityInputInvalid}
          focused={isQuantityInputInvalid}
          fullWidth
          variant={'standard'}
          className={'seats'}
          value={selectionCargoParamaters.baseParameters.quantity}
          disabled={isActionsDisabled}
          onFocus={() => handleChangeCargoSelectionBaseParameters('quantity', parseInt(''))}
          onChange={(event) =>
            handleChangeCargoSelectionBaseParameters('quantity', parseInt(event.target.value))
          }
          type={'number'}
          InputProps={{
            inputMode: 'numeric',
            startAdornment: (
              <InputAdornment
                sx={{ color: '#707070', position: 'absolute', pointerEvents: 'none' }}
                position="start"
              >
                <Typography component={'span'} variant={'h5'}>
                  {t('Common:SeatsPcs')}
                </Typography>
              </InputAdornment>
            ),
          }}
        />
      </Box>
      {isDeletable && (
        <IconButton
          sx={{
            position: 'absolute',
            right: '-15px',
            top: '-15px',
          }}
          size={'small'}
          disabled={isActionsDisabled}
          onClick={onDeleteSelectionCargoParameters}
        >
          <CloseIcon sx={{ fontSize: 22 }} />
        </IconButton>
      )}
    </Box>
  );
};

export default CargoSelectionBase;
